import { useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { ROUTES } from '@/router/routes';
import { Signup } from '@/api/signups';
import { User } from '@/api/users';
import { useGetUserSignups } from '@/hooks/api/use-get-user-signups';
import { useGetUsers } from '@/hooks/api/use-get-users';

import { Section } from '@/components/content-layout/section';
import { SectionHeader } from '@/components/content-layout/section-header';
import { FetchStates } from '@/components/fetch-states';
import { BasicDrawer } from '@/components/ui/basic-drawer';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { SignupsTable } from '@/components/admin/users/signups-table';
import { UsersTable } from '@/components/admin/users/users-table';
import { Badge } from '@/components/ui/badge';

type TabType = 'signups' | 'users';

export function AdminUsersPage() {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [onlyNew, setOnlyNew] = useState(true);

  const { signups, loading, error, refetch } = useGetUserSignups(onlyNew);
  const { users, loading: usersLoading, error: usersError, refetch: refetchUsers } = useGetUsers();

  const isApprovalRoute = location.pathname.includes('/approval/');
  const isEditRoute = location.pathname.includes('/edit/');

  const [activeTab, setActiveTab] = useState<TabType>('users');

  const [isDrawerOpen, setIsDrawerOpen] = useState(() => Boolean(userId));

  const isApproval = isApprovalRoute && userId;
  const isEdit = isEditRoute && userId;

  const drawerTitle = isApproval
    ? t('admin-users-approval-sheet-title')
    : isEdit
      ? t('admin-users-edit-sheet-title')
      : '';

  const handleTabChange = (value: string) => {
    setActiveTab(value as TabType);
    if (isDrawerOpen) {
      navigate(ROUTES.ADMIN_USERS);
      setIsDrawerOpen(false);
    }
  };

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
    navigate(ROUTES.ADMIN_USERS);
  };

  const onFormClose = async () => {
    onDrawerClose();
    if (activeTab === 'signups') {
      await refetch();
    } else {
      await refetchUsers();
    }
  };

  const handleSignupRowClick = (signup: Signup) => {
    if (onlyNew) {
      navigate(ROUTES.ADMIN_USERS_APPROVAL.replace(':userEmail', signup.user_email));
      setIsDrawerOpen(true);
    }
  };

  const handleUserRowClick = (user: User) => {
    navigate(ROUTES.ADMIN_USERS_EDIT.replace(':userId', user.user_email));
    setIsDrawerOpen(true);
  };

  return (
    <>
      <BasicDrawer
        open={isDrawerOpen || Boolean(userId)}
        onClose={onDrawerClose}
        title={drawerTitle}
      >
        <div className="py-4">
          <Outlet
            context={
              { onFormClose } satisfies {
                onFormClose: () => Promise<void>;
              }
            }
          />
        </div>
      </BasicDrawer>

      <Section>
        <SectionHeader title={t('admin-users-title')} element="h1" />

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="users">{t('admin-users-tab-system-users')}</TabsTrigger>
            <TabsTrigger value="signups">
              {t('admin-users-tab-signups')}
              {signups.length > 0 && onlyNew && (
                <Badge color="blue" className="m-1">
                  {signups.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="signups">
            <div className="m-4">
              <Switch
                checked={onlyNew}
                onChange={setOnlyNew}
                label={t('admin-users-signups-only-new')}
              />
            </div>
            <FetchStates
              loading={loading}
              error={error?.message}
              refetch={() => void refetch()}
              empty={!signups || signups.length === 0}
            >
              <div className="page-content">
                <SignupsTable signups={signups} onRowClick={handleSignupRowClick} />
              </div>
            </FetchStates>
          </TabsContent>

          <TabsContent value="users">
            <FetchStates
              loading={usersLoading}
              error={usersError?.message}
              refetch={() => void refetchUsers()}
              empty={!users || users.length === 0}
            >
              <div className="page-content">
                <UsersTable users={users} onRowClick={handleUserRowClick} />
              </div>
            </FetchStates>
          </TabsContent>
        </Tabs>
      </Section>
    </>
  );
}
